import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../admin/AdminDashboard.css";
import "./AdminAdvertisers.css";

import { db, storage } from "../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/* ------------------------------------------------------------
   Available Pages
------------------------------------------------------------ */
const AVAILABLE_PAGES = [
  { id: "home", label: "Home Page" },
  { id: "login", label: "Login" },
  { id: "register", label: "Register" },
  { id: "dashboard", label: "User Dashboard" },
  { id: "trails", label: "Trails List" },
  { id: "startTrail", label: "Start Trail Page" },
  { id: "trailDetail", label: "Trail Detail View" },
  { id: "UsefulLinks", label: "Useful Links Page" },
];

/* ------------------------------------------------------------
   Duration Options
------------------------------------------------------------ */
const DURATION_OPTIONS = [
  { id: "1w", label: "Week", days: 7 },
  { id: "1m", label: "Month", days: 30 },
  { id: "3m", label: "3 Months", days: 90 },
  { id: "6m", label: "6 Months", days: 180 },
  { id: "1y", label: "1 Year", days: 365 },
];

export default function AdminAdvertiserEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [advertiser, setAdvertiser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form fields
  const [name, setName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [isActive, setIsActive] = useState(false);

  // Placement fields
  const [selectedPages, setSelectedPages] = useState([]);
  const [selectedTrails, setSelectedTrails] = useState([]);
  const [availableTrails, setAvailableTrails] = useState([]);

  // Duration
  const [duration, setDuration] = useState("");

  /* ------------------------------------------------------------
     Load advertiser + trails
  ------------------------------------------------------------ */
  useEffect(() => {
    async function loadData() {
      try {
        const refDoc = doc(db, "advertisers", id);
        const snap = await getDoc(refDoc);

        if (!snap.exists()) {
          setError("Advertiser not found.");
          setLoading(false);
          return;
        }

        const data = snap.data();
        setAdvertiser({ id: snap.id, ...data });

        setName(data.name || "");
        setLinkUrl(data.linkUrl || "");
        setIsActive(data.isActive || false);
        setSelectedPages(data.showOnPages || []);
        setSelectedTrails(data.showOnTrails || []);
        setDuration(data.activeDuration || "");

        const trailsSnap = await getDocs(collection(db, "trails"));
        const trailsList = trailsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setAvailableTrails(trailsList);
      } catch (err) {
        console.error(err);
        setError("Failed to load advertiser.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [id]);

  const togglePage = (pageId) => {
    setSelectedPages((prev) =>
      prev.includes(pageId)
        ? prev.filter((p) => p !== pageId)
        : [...prev, pageId]
    );
  };

  const toggleTrail = (trailId) => {
    setSelectedTrails((prev) =>
      prev.includes(trailId)
        ? prev.filter((t) => t !== trailId)
        : [...prev, trailId]
    );
  };

  /* ------------------------------------------------------------
     SAVE advertiser changes
  ------------------------------------------------------------ */
  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    try {
      setSaving(true);

      let newImageUrl = advertiser.imageUrl;

      if (imageFile) {
        const imgRef = ref(
          storage,
          `advertisers/${Date.now()}_${imageFile.name}`
        );
        await uploadBytes(imgRef, imageFile);
        newImageUrl = await getDownloadURL(imgRef);
      }

      let activeUntil = null;

      if (isActive && duration) {
        const d = DURATION_OPTIONS.find((x) => x.id === duration);
        if (d) {
          const now = new Date();
          now.setDate(now.getDate() + d.days);
          activeUntil = now;
        }
      }

      await updateDoc(doc(db, "advertisers", id), {
        name,
        linkUrl,
        imageUrl: newImageUrl,
        isActive,
        showOnPages: selectedPages,
        showOnTrails: selectedTrails,
        activeDuration: duration,
        activeUntil: activeUntil || null,
      });

      navigate("/admin/advertisers");
    } catch (err) {
      console.error(err);
      setError("Failed to save advertiser.");
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------
     RENDER
  ------------------------------------------------------------ */
  if (loading) {
    return (
      <div className="admin-page">
        <h1>Edit Advertiser</h1>
        <p>Loading…</p>
      </div>
    );
  }

  if (!advertiser) {
    return (
      <div className="admin-page">
        <h1>Edit Advertiser</h1>
        <p>{error}</p>
      </div>
    );
  }

  const views = advertiser.views ?? 0;
  const clicks = advertiser.clicks ?? 0;

  return (
    <div className="admin-page">
      <h1>Edit Advertiser</h1>
      <p className="admin-subtitle">
        Manage advertiser details, pages & duration
      </p>

      <div className="section-box">
        <form className="admin-form-large" onSubmit={handleSave}>
          {error && (
            <p style={{ color: "#ff8fb4", marginBottom: "10px" }}>{error}</p>
          )}

          {/* ACTIVE DROPDOWN (Updated) */}
          <label className="admin-label">Active</label>
          <select
            className="admin-input"
            value={isActive ? "active" : "inactive"}
            onChange={(e) => setIsActive(e.target.value === "active")}
            style={{ marginBottom: "18px" }}
          >
            <option value="inactive">Inactive</option>
            <option value="active">Active</option>
          </select>

          {/* DURATION */}
          <label className="admin-label">Active Duration</label>
          <select
            className="admin-input"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          >
            <option value="">Select Duration</option>
            {DURATION_OPTIONS.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>

          {/* NAME */}
          <label className="admin-label">Name</label>
          <input
            type="text"
            className="admin-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          {/* LINK */}
          <label className="admin-label">Link URL</label>
          <input
            type="url"
            className="admin-input"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />

          {/* IMAGE UPLOAD */}
          <label className="admin-label">Replace Banner (optional)</label>
          <input
            type="file"
            className="admin-input"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files[0] || null)}
          />

          <div style={{ textAlign: "center", marginTop: "14px" }}>
            <img
              src={advertiser.imageUrl}
              alt="Current Banner"
              className="advertiser-thumb"
              style={{ width: "140px", borderRadius: "8px" }}
            />
            <p style={{ color: "#aaa", marginTop: "4px" }}>Current Banner</p>
          </div>

          {/* VIEWS & CLICKS */}
          <div
            style={{
              marginTop: "20px",
              padding: "10px",
              borderRadius: "8px",
              background: "rgba(255,255,255,0.06)",
              display: "flex",
              gap: "30px",
            }}
          >
            <div>
              <div style={{ fontSize: "12px", color: "#aaa" }}>
                Total Views
              </div>
              <div style={{ fontSize: "22px", fontWeight: "bold" }}>
                {views}
              </div>
            </div>

            <div>
              <div style={{ fontSize: "12px", color: "#aaa" }}>
                Total Clicks
              </div>
              <div style={{ fontSize: "22px", fontWeight: "bold" }}>
                {clicks}
              </div>
            </div>
          </div>

          {/* PAGE SELECTION */}
          <div style={{ marginTop: "25px" }}>
            <label className="admin-label">Show on Pages</label>

            <ul style={{ marginTop: "10px", paddingLeft: "0", listStyle: "none" }}>
              {AVAILABLE_PAGES.map((page) => (
                <li
                  key={page.id}
                  style={{
                    marginBottom: "8px",
                    background: "rgba(255,255,255,0.06)",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedPages.includes(page.id)}
                    onChange={() => togglePage(page.id)}
                  />
                  <span>{page.label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* TRAIL SELECTION */}
          <div style={{ marginTop: "28px" }}>
            <label className="admin-label">Show on Trails Pages</label>
            <div
              style={{
                maxHeight: "240px",
                overflowY: "auto",
                background: "rgba(255,255,255,0.06)",
                borderRadius: "8px",
                padding: "10px",
                marginTop: "8px",
              }}
            >
              {availableTrails.map((trail) => (
                <label
                  key={trail.id}
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedTrails.includes(trail.id)}
                    onChange={() => toggleTrail(trail.id)}
                    style={{ marginRight: "6px" }}
                  />
                  {trail.name || trail.id}
                </label>
              ))}
            </div>
          </div>

          {/* BUTTONS */}
          <button
            className="admin-button"
            type="submit"
            disabled={saving}
            style={{ marginTop: "20px" }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>

          <button
            type="button"
            className="admin-button secondary"
            onClick={() => navigate("/admin/advertisers")}
            style={{ marginTop: "10px" }}
          >
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
}
